import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Layers, Download, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { PROVIDER_META } from "@/lib/meta";

const modelLabel = (m) => ({ "sora-2": "Sora 2", "sora-2-pro": "Sora 2 Pro", "gpt-image-1": "GPT Image 1", "gemini-3.1-flash-image-preview": "Nano Banana (Flash)", "gemini-3-pro-image-preview": "Nano Banana Pro" }[m] || m);

export default function SharePage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const mediaUrl = `${API}/share/${token}/media`;

  useEffect(() => {
    fetch(`${API}/share/${token}`).then(async (r) => {
      if (!r.ok) throw new Error((await r.json()).detail || "Not found");
      setMeta(await r.json());
    }).catch((e) => setError(e.message));
  }, [token]);

  const badge = meta ? PROVIDER_META[meta.provider]?.badge : "";

  return (
    <div className="min-h-full flex flex-col relative overflow-hidden" data-testid="share-page">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-indigo-600/10 blur-[140px] pointer-events-none" />

      <header className="relative h-16 flex items-center justify-between px-6 lg:px-12">
        <Link to="/" className="flex items-center gap-2.5" data-testid="share-logo">
          <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center"><Layers className="w-4 h-4 text-white" /></div>
          <span className="font-display font-semibold text-slate-100">OmniModel</span>
        </Link>
        <Link to="/login" data-testid="share-cta" className="inline-flex items-center gap-2 text-sm bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-4 py-2 rounded-xl transition-colors">
          Sign in <ArrowRight className="w-4 h-4" />
        </Link>
      </header>

      <main className="relative flex-1 flex items-center justify-center px-4 py-10">
        {error && (
          <div className="text-center rise" data-testid="share-error">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-100">This link isn't available</h1>
            <p className="mt-3 text-sm text-slate-400">{error}. The owner may have revoked it.</p>
          </div>
        )}
        {meta && (
          <div className="w-full max-w-4xl rise">
            <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-3">{meta.kind === "video" ? "AI video" : "AI image"} · shared by {meta.author}</p>
            <h1 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-slate-100 leading-snug" data-testid="share-prompt">{meta.prompt || "Untitled"}</h1>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-800 bg-black shadow-2xl">
              {meta.kind === "video" ? (
                <video src={mediaUrl} controls autoPlay loop playsInline className="w-full max-h-[70vh] mx-auto" data-testid="share-video" />
              ) : (
                <img src={mediaUrl} alt={meta.prompt} className="w-full max-h-[70vh] object-contain mx-auto" data-testid="share-image" />
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full border ${badge || "border-slate-700"}`}>{modelLabel(meta.model)}</span>
                <span>{new Date(meta.created_at).toLocaleDateString()}</span>
              </div>
              <a href={mediaUrl} download data-testid="share-download" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors"><Download className="w-3.5 h-3.5" /> Download</a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
